import { Time } from '@geee-be/core';
import { COOKIE_ACCESS_TOKEN } from '@local/common-lib';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import Cookie from 'js-cookie';
import { jsonDateParser } from 'json-date-parser';
import pRetry from 'p-retry';
import { CodedError, isAxiosError } from '../error.js';
import type { SinglePromise } from '../promise.js';
import { single } from '../promise.js';
import type {
  AuthenticationFailedState,
  AuthenticationState,
  NotAuthenticatedState,
  Token,
  TokenProviderOptions,
} from './types.js';
import { AuthStatus } from './types.js';
import { decodeToken } from './util.js';

const DEFAULT_RETRY_COUNT = 5;

interface AuthSyncChannel {
  addEventListener(type: 'message', listener: EventListener): void;
  postMessage(message: unknown): Promise<void>;
  removeEventListener(type: 'message', listener: EventListener): void;
}

class NoopAuthSyncChannel implements AuthSyncChannel {
  addEventListener(): void {}

  removeEventListener(): void {}

  postMessage(): Promise<void> {
    return Promise.resolve();
  }
}

const createAuthSyncChannel = (): AuthSyncChannel => {
  if (typeof globalThis.BroadcastChannel !== 'function') {
    return new NoopAuthSyncChannel();
  }

  try {
    const channel = new globalThis.BroadcastChannel('auth-sync');
    return {
      addEventListener: (type, listener) => {
        channel.addEventListener(type, listener);
      },
      postMessage: (message) => {
        channel.postMessage(message);
        return Promise.resolve();
      },
      removeEventListener: (type, listener) => {
        channel.removeEventListener(type, listener);
      },
    };
  } catch (error) {
    console.warn('BroadcastChannel unavailable, auth sync disabled', error);
    return new NoopAuthSyncChannel();
  }
};

const decodeAccessToken = (): Token | null => {
  const accessTokenCookie = Cookie.get(COOKIE_ACCESS_TOKEN);
  if (!accessTokenCookie) return null;

  return decodeToken(accessTokenCookie);
};

export class TokenProvider<
  O extends TokenProviderOptions = TokenProviderOptions,
> {
  protected single: SinglePromise;

  private authSync: AuthSyncChannel;
  private remoteAuthMessageHandler?: EventListener;

  constructor(protected readonly options: O) {
    this.single = single();

    this.authSync = createAuthSyncChannel();
    this.watchRemoteTabAuthChanges();
    this.refresh().catch(() => {});
  }

  public signIn(
    email: string,
    password: string,
    remember: boolean,
    sharedDevice: boolean,
  ): Promise<boolean> {
    return this.singleTokenRequest(
      'sign-in',
      '/api/v1/auth/sign-in',
      { email, password, remember, sharedDevice },
      'User signed in successfully',
    );
  }

  /**
   * Moves this session to another organization.
   *
   * Unlike sign in, a failure here must not tear the session down - being
   * refused an organization leaves the current one perfectly valid - so the
   * error is thrown for the caller to report.
   */
  public async switchOrganization(organizationId: string): Promise<void> {
    await this.single('switch-organization', async () => {
      await axios.post('/api/v1/auth/switch-organization', { organizationId });
      this.saveAccessToken();
      this.options.logger?.verbose('Switched organization');
      return true;
    });
  }

  public async refresh(): Promise<boolean> {
    const result = await this.singleTokenRequest(
      'refresh',
      '/api/v1/auth/refresh',
      {},
      'Service token refreshed',
    );

    const accessTokenCookie = Cookie.get(COOKIE_ACCESS_TOKEN);
    if (!accessTokenCookie) {
      this.setAuthToNotAuthenticated();
    }

    return result;
  }

  public async signOut(): Promise<void> {
    await this.single('signOut', async () => {
      return axios.post('/api/v1/auth/sign-out');
    });

    // this.updateRemoteAndLocalAuth(null);
    this.updateRemoteAuthSigningOut();
  }

  protected throwError(message: string, cause?: unknown): never {
    const error = new Error(message);
    this.options.logger?.error(cause ?? error);
    throw error;
  }

  protected async refreshAccessToken(): Promise<unknown> {
    return this.single('refreshAccessToken', () =>
      pRetry(
        async () => {
          const accessToken = decodeAccessToken();
          if (!accessToken) return this.refresh();
          if (
            isTokenExpiringIn(
              accessToken.expires,
              this.options.thresholdBeforeExpire,
            )
          ) {
            return this.refresh();
          }

          if (
            isTokenExpiringIn(
              accessToken.expires,
              this.options.thresholdBeforeEarlyRefresh ?? Time.MINUTE,
            )
          ) {
            // intentionally not awaited
            this.refresh().catch((error) => {
              this.options.logger?.warn('Preliminary refresh failed', error);
            });
          }
        },
        {
          onFailedAttempt: () =>
            this.options.logger?.verbose('Retry getting accessToken'),
          retries: this.options.retryCount ?? DEFAULT_RETRY_COUNT,
        },
      ).catch((error) => {
        const accessToken = decodeAccessToken();
        if (!accessToken) return this.throwError('Token create failed', error);
        this.throwError('Token refresh failed', error);
      }),
    );
  }

  /**
   * Creates an authenticated axios instance
   * @param axiosOptions.incomingAuthorization Authorization header from incoming request
   */
  public axios(config?: AxiosRequestConfig): AxiosInstance {
    const customAxios = axios.create({
      ...config,
      transformResponse: [
        (response: string) =>
          JSON.parse(response, jsonDateParser) as Record<string, unknown>,
      ],
    });
    // intentionally not awaited and intentional empty-catch as it logs and rethrows error already
    this.refreshAccessToken().catch(() => {});

    customAxios.interceptors.request.use(async (options) => {
      await this.refreshAccessToken();
      return options;
    });
    return customAxios;
  }

  protected singleTokenRequest(
    key: string,
    url: string,
    data: Record<string, unknown>,
    logMessage: string,
  ): Promise<boolean> {
    return this.single(key, async () => {
      await axios.post<{ token: string }>(url, data);
      this.saveAccessToken();
      this.options.logger?.verbose(logMessage);
      return true;
    }).catch((error: Error) => {
      this.setAuthToAuthenticationFailed(error);
      return false;
    });
  }

  protected saveAccessToken(): void {
    const payload = decodeAccessToken();
    if (!payload) {
      // Returning without setting any state leaves the app on its loading
      // spinner forever, with nothing in the console. Fail to signed-out.
      const error = new CodedError(
        'Access token could not be read',
        'invalid-access-token',
      );
      this.options.logger?.warn(error.message);
      this.setAuthToNotAuthenticated(error);
      return;
    }

    this.updateRemoteAndLocalAuth();
  }

  private watchRemoteTabAuthChanges(): void {
    this.remoteAuthMessageHandler = (): void => {
      this.updateLocalAuth();
    };
    // Auth set remotely, e.g. another tab
    this.authSync.addEventListener('message', this.remoteAuthMessageHandler);
  }

  public cleanup(): void {
    if (!this.remoteAuthMessageHandler) return;

    this.authSync.removeEventListener('message', this.remoteAuthMessageHandler);
  }

  public errorHandler(error: Error): void {
    if (isAxiosError(error)) {
      switch (error.response?.status) {
        case 400:
          if (
            error.response.data &&
            typeof error.response.data === 'object' &&
            'error' in error.response.data &&
            'error_description' in error.response.data
          ) {
            this.options.setAuth({
              failure: {
                error: String(error.response.data.error ?? 'unauthorized'),
                errorDescription: String(error.response.data.error_description),
              },
              status: AuthStatus.AUTHENTICATION_FAILED,
            });
            return;
          }
          return;
        case 401:
          this.options.setAuth({
            failure: {
              error: 'unauthorized',
              errorDescription: error.response.statusText,
            },
            status: AuthStatus.AUTHENTICATION_FAILED,
          });
          return;
        case 403:
          this.options.setAuth({
            failure: {
              error: 'forbidden',
              errorDescription: error.response.statusText,
            },
            status: AuthStatus.AUTHENTICATION_FAILED,
          });
          return;
      }
    }
    this.options.setAuth({
      error,
      status: AuthStatus.CONFIG_ERROR,
    });
    console.error(error);
  }

  private updateLocalAuth(): void {
    this.options.setAuth(this.getAuthenticationState());
  }

  private updateRemoteAndLocalAuth(): void {
    this.authSync.postMessage({}).catch(console.error);
    this.updateLocalAuth();
  }

  private updateRemoteAuthSigningOut(): void {
    this.authSync.postMessage(null).catch(console.error);
    const state: NotAuthenticatedState = {
      loading: false,
      status: AuthStatus.NOT_AUTHENTICATED,
    };
    this.options.setAuth(state);
  }

  private setAuthToNotAuthenticated(error?: CodedError): NotAuthenticatedState {
    const state: NotAuthenticatedState = {
      error,
      loading: false,
      status: AuthStatus.NOT_AUTHENTICATED,
    };
    this.options.setAuth(state);
    return state;
  }

  private setAuthToAuthenticationFailed(
    error?: Error | CodedError,
  ): AuthenticationFailedState {
    const state: AuthenticationFailedState = {
      failure: {
        error: (error instanceof CodedError && error?.code) || 'unknown',
        errorDescription: error?.message ?? 'Authentication failed',
      },
      status: AuthStatus.AUTHENTICATION_FAILED,
    };
    this.options.setAuth(state);
    return state;
  }

  private getAuthenticationState = (): AuthenticationState => {
    const accessToken = decodeAccessToken();

    if (!accessToken) {
      return this.setAuthToNotAuthenticated(
        new CodedError('Access token not present', 'no-access-token'),
      );
    }

    return {
      axios: this.axios(),
      loading: false,
      refreshExpires: accessToken.refreshExpires,
      status: AuthStatus.AUTHENTICATED,
      timestamp: accessToken.payload.iat * 1000,
      userInfo: {
        hasRole: (...anyOfRole: string[]): boolean => {
          const roles = new Set(accessToken.payload.rls);
          return anyOfRole.reduce<boolean>(
            (acc, item) => acc || roles.has(item),
            false,
          );
        },
        isSuperAdmin: accessToken.payload.sa === true,
        organizationId: accessToken.payload.oid,
        userId: accessToken.payload.sub,
      },
    };
  };
}

export const isTokenExpiringIn = (
  expires: number,
  threshold: number,
): boolean => {
  return expires - threshold < Date.now();
};
